import { Container, Grid, Typography } from "@mui/material";
import { useEffect, useState, useRef, createRef } from "react";
import { motion } from "framer-motion";
import { useTheme } from "@emotion/react";

import GridBG from "../common/GridBG";
import RelationSVG from "./RelationSVG";
import longArrowDown from "../../assets/svg/longArrowDown.svg";
import bigOBlue from "../../assets/svg/bigOBlue.svg";

import { mediaQueryBreakPoints } from "../../utils/functions";

import { relationBulletPoint, containerGrid, 
  methodologyInnerBulletPoint, headingSBluetec, mb20, headingLBlack, 
  bodyLeadBlack, methodologyHeader, bodyLeadBluetec, relationArrowContainer, alignSelfFlexStart } from "../../theme/styles";
import { useAppRefData } from "../../contexts/Contexts";

export default function Relation({data, width, height, elementRef}) {
  const [step, setStep] = useState(0)
  const [process, setProcess] = useState(data.relations[0])

  const bulletRefs = useRef(data.relations.map(() => createRef()));
  const refStart = useRef(null)
  const refHeader = useRef(null)
  const theme = useTheme()
  const prevScrollY = useRef(0)
  const appRefContext = useAppRefData()
  const initialScroll = elementRef.current?.offsetTop

  const debounce = (func, delay) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), delay);
    };
  };

  useEffect(() => {
    const disableScroll = () => {
      document.body.style.overflow = 'hidden';
    };

    const enableScroll = () => {
      document.body.style.overflow = '';
    };

    const setElementStyles = (isSticky) => {
      Array.from(appRefContext.appRef.current.children).forEach((c, i) => {
        if (i > 1) {
          c.style.position = isSticky ? 'sticky' : 'relative';
          c.style.top = isSticky ? '2000px' : 'unset';
        }
      });

      elementRef.current.style.position = isSticky ? 'sticky' : 'relative';
      elementRef.current.style.top = isSticky ? '0px' : 'unset';

      if (isSticky) {
        elementRef.current.nextSibling.style.position = 'sticky';
        elementRef.current.nextSibling.style.top = `${elementRef.current.clientHeight}px`;
      } else {
        elementRef.current.nextSibling.style.position = 'relative';
        elementRef.current.nextSibling.style.top = 'unset';
      }
    };

    const handleScroll = () => {
      const currentScrollY = document.documentElement.scrollTop;
      const elementOffsetTop = elementRef.current?.offsetTop;
      const scrollDelta = document.documentElement.scrollTop - elementOffsetTop;
      if (scrollDelta > -10 && step < (data.relations.length - 1)) {
        disableScroll();

        setTimeout(enableScroll, 1000);

        if (currentScrollY > prevScrollY.current) {
          setElementStyles(true);

          if (step < data.relations?.length - 1) {
            setStep((prevStep) => prevStep + 1);
            window.scrollTo({ top: initialScroll, behavior: 'instant' });
          } else {
            setElementStyles(false);
          }
        }

        prevScrollY.current = currentScrollY;
      } else if (scrollDelta < -10 && currentScrollY < prevScrollY.current) {
        setElementStyles(false);
        enableScroll()
        if (step > 0) {
          setStep(0);
        }

        prevScrollY.current = currentScrollY;
      } else {
        setElementStyles(false);
        enableScroll()
      }
    };

    const debouncedScroll = debounce(handleScroll, 50);

    window.addEventListener('scroll', debouncedScroll);

    return () => {
      window.removeEventListener('scroll', debouncedScroll);
    };
  }, [step, data, appRefContext.appRef, elementRef, initialScroll]);

  useEffect(() => {
    setProcess(data.relations[step])
  }, [step, data])

  const calculateTranslate = () => {
    if (bulletRefs.current[step] && bulletRefs.current[step].current) {
      const bulletTop = bulletRefs.current[step].current.offsetTop;
      const bulletHeight = bulletRefs.current[step].current.clientHeight;
      const headerHeight = refHeader.current.clientHeight;
      const headerTop = refHeader.current.offsetTop + refHeader.current.parentNode.parentNode.offsetTop;
      
      const bulletMiddle = bulletTop + (bulletHeight / 2);
      const headerMiddle = headerTop + (headerHeight / 2);
      const translation = bulletMiddle - headerMiddle;
      
      return translation;
    } 
    return 0;
  };

  return (
    <>
      <GridBG width={width} height={height} color={theme.palette.bluetec20} pattern={'void'}/>
      {data && 
        <Grid container columns={width > mediaQueryBreakPoints.m ? 12: 8} sx={containerGrid}>
          {width > mediaQueryBreakPoints.m &&
            <Grid item xs={1} sx={width > mediaQueryBreakPoints.xl ? {} : [alignSelfFlexStart, {marginTop: '30px'}]}>
              <Container maxWidth={false} disableGutters ref={refStart}>
              {data.relations?.map((item, index) => {
                return (
                  index === 0 ? 
                  <div key={index} ref={bulletRefs.current[index]}/> : 
                  <div key={index} style={relationBulletPoint} ref={bulletRefs.current[index]}>
                    <div style={methodologyInnerBulletPoint}/>
                  </div>
                )
              })}
              </Container>
            </Grid>
          }
           <Grid item s={4} xxs={12} sx={width > mediaQueryBreakPoints.xl ? {} : [alignSelfFlexStart]}>
            <Container maxWidth={false} disableGutters sx={[methodologyHeader, width > mediaQueryBreakPoints.m && {transform: `translate(0, ${calculateTranslate()}px)`}]}>
              <motion.div key={step} initial={{opacity: 0}} animate={{opacity: 1}} transition={{duration: .8}} exit={{opacity: 0}}>
                <Typography sx={[headingSBluetec, mb20]}>{process?.subtitle}</Typography>
                <Typography ref={refHeader} sx={[headingLBlack, mb20]}>{process?.title}</Typography>
                <Typography sx={bodyLeadBlack}>{process?.description}</Typography>
              </motion.div>
            </Container>
            {width > mediaQueryBreakPoints.xs &&
              <Container maxWidth={false} disableGutters sx={relationArrowContainer}>
                <Typography sx={bodyLeadBluetec}>{data.bottomText}</Typography>
                <img alt={""} src={longArrowDown} />
              </Container>
            }
          </Grid>
          <Grid item l={1} s={1}/>
          <Grid item l={6} s={5}>
            <Container maxWidth={false} disableGutters sx={width < mediaQueryBreakPoints.m ? {marginTop: '60px'} : {}}>
              <RelationSVG 
                width={width}
                height={width > mediaQueryBreakPoints.m ? height : height/2} 
                rectHeight={width > mediaQueryBreakPoints.xl ? 2 : width < mediaQueryBreakPoints.m ? 3 : 2}
                rectStart={width > mediaQueryBreakPoints.xl ? .5 : 0}
                gridColumns={width > mediaQueryBreakPoints.m ? 12 : width < mediaQueryBreakPoints.xs ? 6 : 8}
                step={step} 
                bigO={bigOBlue}
                />
            </Container>
          </Grid>
        </Grid>
      }
    </>
  );
};